import React, { useMemo } from 'react';
import AgChartsWrapper from '../AgChartsWrapper';
import type { AgChartOptions } from '../AgChartsWrapper';
import type { ThemeMode } from '../../../../../types';
import {
  DEFAULT_COLORS,
  buildAgChartTheme,
  buildCartesianAxes,
  buildMultiAxes,
  computeAxisLayout,
  computeFixedRange,
  buildEndValueTags,
  buildLegendConfig,
  SERIES_DEFAULTS,
} from '../../../design';
import type { AxisLabelFormatter, ChartTooltipParams, ChartTooltipResult } from '../../../design';

export interface BarChartSeries {
  yKey: string;
  yName: string;
  color?: string;
  /** Axis ID — series sharing the same ID share the same Y axis */
  axis?: string;
  /** Color each bar by its value's sign (e.g. earnings beats green, misses
   *  red). Overrides `color` per datum. */
  fillBySign?: { positive: string; negative: string };
  tooltipRenderer?: (params: ChartTooltipParams) => ChartTooltipResult;
}

export interface OverlaySeries {
  type: 'line' | 'area';
  yKey: string;
  yName: string;
  color?: string;
  axis?: string;
  strokeWidth?: number;
  fillOpacity?: number;
  markers?: boolean;
}

export interface BarChartProps {
  data: Record<string, unknown>[];
  xKey: string;
  yKey?: string;
  series?: BarChartSeries[];
  overlays?: OverlaySeries[];
  colors?: string[];
  title?: string;
  legend?: boolean;
  legendValues?: boolean;
  grouped?: boolean;
  normalizedTo?: number;
  showEndValueTags?: boolean;
  endValueFormatter?: (v: number) => string;
  xLabelFormatter?: AxisLabelFormatter;
  yLabelFormatters?: Record<string, AxisLabelFormatter>;
  yLabelFormatter?: AxisLabelFormatter;
  /** X-axis title. */
  xTitle?: string;
  /** Y-axis titles keyed by axis id (single-axis charts use the first id). */
  yTitles?: Record<string, string>;
  hiddenSeries?: Set<string>;
  theme?: ThemeMode;
}

const BarChart: React.FC<BarChartProps> = ({
  data,
  xKey,
  yKey,
  series: seriesProp,
  overlays,
  colors: colorsProp,
  title,
  legend,
  legendValues,
  grouped = true,
  normalizedTo,
  showEndValueTags,
  endValueFormatter,
  xLabelFormatter,
  yLabelFormatters,
  yLabelFormatter,
  xTitle,
  yTitles,
  hiddenSeries,
  theme: themeMode = 'light',
}) => {
  const chartOptions = useMemo(() => {
    const colors = colorsProp || DEFAULT_COLORS;

    const allBarSeries: BarChartSeries[] = seriesProp
      || (yKey ? [{ yKey, yName: yKey }] : []);
    const allOverlays = overlays || [];

    const colorMap = new Map<string, string>();
    [...allBarSeries, ...allOverlays].forEach((s, i) => {
      colorMap.set(s.yKey, s.color || colors[i % colors.length]);
    });

    const seriesList = hiddenSeries?.size
      ? allBarSeries.filter((s) => !hiddenSeries.has(s.yKey))
      : allBarSeries;

    const filteredOverlays = hiddenSeries?.size
      ? allOverlays.filter((s) => !hiddenSeries.has(s.yKey))
      : allOverlays;

    const allSeries = [...seriesList, ...filteredOverlays];

    // Compute axis layout from ALL series (before filtering) for stable axes
    const allAxisIds = [...allBarSeries, ...allOverlays].map((s) => s.axis || 'a');
    const uniqueAxisIds = Array.from(new Set(allAxisIds));
    const isMultiAxis = uniqueAxisIds.length > 1;

    const axisLayout = isMultiAxis ? computeAxisLayout(uniqueAxisIds) : null;
    const hasLeftAxis = isMultiAxis && !!axisLayout && Array.from(axisLayout.values()).some((l) => l.position === 'left');
    const hasRightAxis = !isMultiAxis || !axisLayout || Array.from(axisLayout.values()).some((l) => l.position === 'right');
    const agTheme = buildAgChartTheme(colors, themeMode, { hasLeftAxis, hasRightAxis, hasEndValueTags: showEndValueTags });

    // Compute fixed ranges from ALL series (so hiding doesn't rescale)
    // Only bar series stack; overlays are independent
    const isStacked = !grouped;
    const fixedRanges: Record<string, { min: number; max: number }> = {};
    for (const axisId of uniqueAxisIds) {
      const barKeysOnAxis = allBarSeries
        .filter((s) => (s.axis || 'a') === axisId)
        .map((s) => s.yKey);
      const overlayKeysOnAxis = allOverlays
        .filter((s) => (s.axis || 'a') === axisId)
        .map((s) => s.yKey);
      const barRange = computeFixedRange(data, barKeysOnAxis, isStacked);
      if (overlayKeysOnAxis.length > 0) {
        const overlayRange = computeFixedRange(data, overlayKeysOnAxis);
        fixedRanges[axisId] = {
          min: Math.min(barRange.min, overlayRange.min),
          max: Math.max(barRange.max, overlayRange.max),
        };
      } else {
        fixedRanges[axisId] = barRange;
      }
    }

    // Build axes
    let axes: Record<string, unknown>;
    if (isMultiAxis) {
      const layout = computeAxisLayout(uniqueAxisIds);
      const fmts: Record<string, AxisLabelFormatter> = { ...yLabelFormatters };
      if (yLabelFormatter && !fmts[uniqueAxisIds[0]]) {
        fmts[uniqueAxisIds[0]] = yLabelFormatter;
      }
      axes = buildMultiAxes({ layout, mode: themeMode, xLabelFormatter, xTitle, yTitles, yLabelFormatters: fmts, fixedRanges });
    } else {
      axes = buildCartesianAxes({
        xLabelFormatter,
        yLabelFormatter: yLabelFormatter || yLabelFormatters?.[uniqueAxisIds[0]],
        xTitle,
        yTitle: yTitles?.[uniqueAxisIds[0]],
        yPosition: 'right',
        fixedRange: fixedRanges[uniqueAxisIds[0]],
      });
    }

    // End-value tags via crossLines
    if (showEndValueTags) {
      const barSeriesForTags = seriesList.map((s) => ({
        yKey: s.yKey, yName: s.yName, color: colorMap.get(s.yKey),
      }));
      const overlaySeriesForTags = filteredOverlays.map((s) => ({
        yKey: s.yKey, yName: s.yName, color: colorMap.get(s.yKey),
      }));
      const barCrossLines = buildEndValueTags(data, barSeriesForTags, colors, endValueFormatter, 'right', isStacked);
      const overlayCrossLines = buildEndValueTags(data, overlaySeriesForTags, colors, endValueFormatter, 'right');
      const allCrossLines = [...barCrossLines, ...overlayCrossLines];
      if (allCrossLines.length > 0) {
        const yAxis = axes.y as Record<string, unknown> | undefined;
        if (yAxis) yAxis.crossLines = allCrossLines;
      }
    }

    const layout = isMultiAxis ? computeAxisLayout(uniqueAxisIds) : null;

    // Bar series
    const agBarSeries = seriesList.map((s) => {
      const color = colorMap.get(s.yKey)!;
      const axisId = s.axis || 'a';
      const axisBinding = layout ? { yKeyAxis: layout.get(axisId)!.key } : {};
      const tooltipOpts = s.tooltipRenderer ? { tooltip: { renderer: s.tooltipRenderer } } : {};

      return {
        type: 'bar' as const,
        xKey,
        yKey: s.yKey,
        yName: s.yName,
        fill: color,
        cornerRadius: SERIES_DEFAULTS.bar.cornerRadius,
        ...(s.fillBySign
          ? {
              itemStyler: (params: { datum?: Record<string, unknown> }) => {
                const v = params.datum?.[s.yKey];
                return {
                  fill: typeof v === 'number' && v < 0 ? s.fillBySign!.negative : s.fillBySign!.positive,
                };
              },
            }
          : {}),
        ...(!grouped ? { stacked: true } : {}),
        ...(normalizedTo ? { normalizedTo } : {}),
        ...axisBinding,
        ...tooltipOpts,
      };
    });

    // Overlay series
    const agOverlaySeries = filteredOverlays.map((s) => {
      const color = colorMap.get(s.yKey)!;
      const axisId = s.axis || 'a';
      const axisBinding = layout ? { yKeyAxis: layout.get(axisId)!.key } : {};

      const base: Record<string, unknown> = {
        type: s.type,
        xKey,
        yKey: s.yKey,
        yName: s.yName,
        ...axisBinding,
      };

      if (s.type === 'area') {
        base.fill = color;
        base.stroke = color;
        base.fillOpacity = s.fillOpacity ?? SERIES_DEFAULTS.area.fillOpacity;
        base.strokeWidth = s.strokeWidth ?? SERIES_DEFAULTS.area.strokeWidth;
        base.marker = { enabled: s.markers ?? false };
      } else {
        base.stroke = color;
        base.strokeWidth = s.strokeWidth ?? SERIES_DEFAULTS.line.strokeWidth;
        base.marker = { enabled: s.markers ?? false };
      }

      return base;
    });

    // Legend
    const legendConfig = buildLegendConfig({
      enabled: legend === true || (legend !== false && allSeries.length > 1),
      position: 'bottom',
      legendValues,
      data,
      series: allSeries,
    });

    return {
      theme: agTheme,
      data,
      title: title ? { text: title } : undefined,
      tooltip: { mode: 'shared' as const },
      legend: legendConfig,
      axes,
      series: [...agBarSeries, ...agOverlaySeries],
    };
  }, [data, xKey, yKey, seriesProp, overlays, colorsProp, title, legend, legendValues, grouped, normalizedTo, showEndValueTags, endValueFormatter, xLabelFormatter, yLabelFormatter, yLabelFormatters, xTitle, yTitles, hiddenSeries, themeMode]);

  return <AgChartsWrapper options={chartOptions as unknown as AgChartOptions} />;
};

export default BarChart;
